import { zodResolver } from '@hookform/resolvers/zod'
import { Button } from 'primereact/button'
import { InputText } from 'primereact/inputtext'
import { Message } from 'primereact/message'
import { useId, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { z } from 'zod'
import { useForgotPasswordMutation } from '~/hooks/authApi'
import useToast from '~/hooks/toast'
import type { ErrorObj } from '~/utils/httpClient'

const forgotPasswordSchema = z.object({
  email: z.string().email('Invalid email address'),
})

const ForgotPasswordPage = () => {
  const navigate = useNavigate()
  const toast = useToast()
  const emailId = useId()
  const [submitted, setSubmitted] = useState(false)

  const forgotMutation = useForgotPasswordMutation()

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<z.infer<typeof forgotPasswordSchema>>({
    resolver: zodResolver(forgotPasswordSchema),
  })

  const onSubmit = (data: z.infer<typeof forgotPasswordSchema>) => {
    forgotMutation.mutate(data.email, {
      onSuccess: () => {
        setSubmitted(true)
        toast?.current?.show({
          severity: 'success',
          summary: 'Request Sent',
          detail: 'If an account exists for that email, you will receive a reset link.',
          life: 5000,
        })
      },
      onError: (oe: unknown) => {
        const e = oe as ErrorObj
        toast?.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: e.message || 'Failed to send reset link',
        })
      },
    })
  }

  return (
    <div className='flex flex-col justify-center items-center min-h-screen w-full bg-teal-200'>
      <div className='w-60 md:w-80 p-4 py-6 rounded-md shadow-md bg-white'>
        <h1 className='font-bold text-center text-2xl mb-6'>Forgot Password</h1>

        {submitted ? (
          <div className='text-center'>
            <p className='mb-4'>Please check your email for the reset link.</p>
            <Button
              label='Return to Login'
              className='p-button-text'
              onClick={() => navigate('/auth/login')}
            />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className='p-fluid'>
            <div className='field my-4'>
              <span className='p-float-label'>
                <InputText
                  id={emailId}
                  {...register('email')}
                  className={errors.email ? 'p-invalid' : ''}
                />
                <label htmlFor={emailId}>Email</label>
              </span>
              {errors.email && (
                <Message severity='error' text={errors.email.message} className='mt-2' />
              )}
            </div>

            <Button
              type='submit'
              label='Send Reset Link'
              className='mt-4'
              loading={forgotMutation.isLoading}
            />

            <div className='text-center mt-4'>
              <a
                href='/auth/login'
                className='text-teal-600 hover:underline cursor-pointer'
                onClick={(e) => {
                  e.preventDefault()
                  navigate('/auth/login')
                }}
              >
                Back to Login
              </a>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}

export default ForgotPasswordPage
